import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { authService } from '@/services/auth.service';
import { SupabaseAuthService } from '@/services/supabase-auth.service';
import { ApiAuthService } from '@/services/api-auth.service';
import { StorageService } from '@/services/storage.service';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Icon } from "@/components/ui/icon"

const REDIRECT_DELAY = 3000;

function getAuthErrorMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  const apiMsg = ax?.response?.data?.message;
  if (apiMsg) {
    if (apiMsg.includes('invalid_grant') || apiMsg.includes('redirectUri'))
      return 'O link de login expirou ou já foi usado. Tente fazer login novamente.';
    return apiMsg;
  }
  if (err instanceof Error) {
    const msg = err.message;
    if (msg.includes('invalid_grant') || msg.includes('Bad Request'))
      return 'O link de login expirou ou já foi usado. Tente fazer login novamente.';
    return msg;
  }
  return 'Erro ao processar autenticação. Tente novamente.';
}

// Evita processamento duplo (React Strict Mode executa useEffect 2x em dev)
let callbackInProgress = false;

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuthStore();
  const [error, setError] = useState('');

  useEffect(() => {
    if (callbackInProgress) return;
    callbackInProgress = true;

    const handleCallback = async () => {
      try {
        // Google pode redirecionar com ?error= quando usuário cancela ou há falha
        const googleError = searchParams.get('error');
        if (googleError) {
          const desc = searchParams.get('error_description') || '';
          const msg =
            googleError === 'access_denied'
              ? 'Login cancelado. Tente novamente quando quiser.'
              : desc
                ? decodeURIComponent(desc.replace(/\+/g, ' '))
                : 'Erro ao autenticar com o Google. Tente novamente.';
          throw new Error(msg);
        }

        // Fluxo direto Google OAuth: ?code=xxx&state=xxx
        const code = searchParams.get('code');
        if (code) {
          // redirectUri deve ser exatamente a URL do callback (sem query params)
          const redirectUri = `${window.location.origin}${window.location.pathname}`;
          const authResponse = await authService.handleOAuthCallback(
            code,
            searchParams.get('state') || undefined,
            redirectUri,
          );
          StorageService.setAccessToken(authResponse.accessToken);
          StorageService.setRefreshToken(authResponse.refreshToken);
          StorageService.setUser(authResponse.user);
          useAuthStore.setState({ user: authResponse.user, initialized: true, loading: false });
          const returnTo = searchParams.get('returnTo') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('LIZTA_RETURN_AFTER_LOGIN') : null);
          try {
            sessionStorage.removeItem('LIZTA_RETURN_AFTER_LOGIN');
            if (returnTo?.startsWith('/') && returnTo.includes('/panelinha/convite/')) {
              sessionStorage.setItem('LIZTA_SHOW_INVITE_TOAST', '1');
            }
          } catch (_) {}
          // Deferir navigate para garantir que React processe o setState antes da navegação
          await new Promise((r) => setTimeout(r, 0));
          navigate(returnTo?.startsWith('/') ? returnTo : '/', { replace: true });
          return;
        }

        // Supabase magic link: fluxo implícito usa hash (#access_token=...), PKCE usa query (?token=...)
        const hashParams = typeof window !== 'undefined' && window.location.hash
          ? new URLSearchParams(window.location.hash.substring(1))
          : null;
        const hasHashTokens = hashParams?.get('access_token') && hashParams?.get('type');
        const tokenFromQuery = searchParams.get('token');
        const tokenType = searchParams.get('type') || hashParams?.get('type') || 'magiclink';
        const emailFromQuery = searchParams.get('email');

        // Fluxo implícito: tokens no hash — Supabase já estabeleceu sessão (detectSessionInUrl)
        if (hasHashTokens) {
          const supabaseAuth = new SupabaseAuthService();
          // Pequena espera para Supabase processar o hash (detectSessionInUrl)
          await new Promise((r) => setTimeout(r, 150));
          const { data: sessionData, error: sessionError } = await supabaseAuth.getSession();

          if (sessionError || !sessionData.session?.user?.email) {
            throw new Error('Não foi possível obter sessão. Tente fazer login novamente.');
          }

          const apiAuth = new ApiAuthService();
          const authResponse = await apiAuth.verifyMagicLinkSession(
            sessionData.session.user.email,
            sessionData.session.user.id,
            sessionData.session.user.user_metadata?.name,
            sessionData.session.user.user_metadata?.avatar_url,
          );

          StorageService.setAccessToken(authResponse.accessToken);
          StorageService.setRefreshToken(authResponse.refreshToken);
          StorageService.setUser(authResponse.user);
          useAuthStore.setState({ user: authResponse.user, initialized: true, loading: false });
          const returnTo = searchParams.get('returnTo') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('LIZTA_RETURN_AFTER_LOGIN') : null);
          try {
            sessionStorage.removeItem('LIZTA_RETURN_AFTER_LOGIN');
            if (returnTo?.startsWith('/') && returnTo.includes('/panelinha/convite/')) {
              sessionStorage.setItem('LIZTA_SHOW_INVITE_TOAST', '1');
            }
          } catch (_) {}
          navigate(returnTo?.startsWith('/') ? returnTo : '/', { replace: true });
          return;
        }

        // Fluxo PKCE ou link com token em query: ?token=...&email=...
        if (tokenFromQuery && emailFromQuery) {
          await verifyMagicLink(emailFromQuery, tokenFromQuery, tokenType);
          useAuthStore.setState({ initialized: true, loading: false });
          const returnTo = searchParams.get('returnTo') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('LIZTA_RETURN_AFTER_LOGIN') : null);
          try {
            sessionStorage.removeItem('LIZTA_RETURN_AFTER_LOGIN');
            if (returnTo?.startsWith('/') && returnTo.includes('/panelinha/convite/')) {
              sessionStorage.setItem('LIZTA_SHOW_INVITE_TOAST', '1');
            }
          } catch (_) {}
          navigate(returnTo?.startsWith('/') ? returnTo : '/', { replace: true });
          return;
        }

        // Token em query mas sem email — tentar obter sessão do Supabase
        if (tokenFromQuery) {
          const supabaseAuth = new SupabaseAuthService();
          const { data: sessionData, error: sessionError } = await supabaseAuth.getSession();

          if (!sessionError && sessionData.session?.user?.email) {
            const apiAuth = new ApiAuthService();
            const authResponse = await apiAuth.verifyMagicLinkSession(
              sessionData.session.user.email,
              sessionData.session.user.id,
            );

            StorageService.setAccessToken(authResponse.accessToken);
            StorageService.setRefreshToken(authResponse.refreshToken);
            StorageService.setUser(authResponse.user);
            useAuthStore.setState({ user: authResponse.user, initialized: true, loading: false });
            const returnTo = searchParams.get('returnTo') || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('LIZTA_RETURN_AFTER_LOGIN') : null);
            try {
              sessionStorage.removeItem('LIZTA_RETURN_AFTER_LOGIN');
              if (returnTo?.startsWith('/') && returnTo.includes('/panelinha/convite/')) {
                sessionStorage.setItem('LIZTA_SHOW_INVITE_TOAST', '1');
              }
            } catch (_) {}
            navigate(returnTo?.startsWith('/') ? returnTo : '/', { replace: true });
            return;
          }
        }

        toast.error('Link de autenticação inválido ou expirado');
        setError('Link de autenticação inválido ou expirado');
        setTimeout(() => navigate('/', { replace: true }), REDIRECT_DELAY);
      } catch (err: unknown) {
        const errorMessage = getAuthErrorMessage(err);
        toast.error(errorMessage);
        setError(errorMessage);
        setTimeout(() => navigate('/', { replace: true }), REDIRECT_DELAY);
      } finally {
        callbackInProgress = false;
      }
    };

    handleCallback();
  }, [searchParams, verifyMagicLink, navigate]);

  const handleManualRedirect = () => {
    navigate('/', { replace: true });
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <header className="flex items-center justify-between border-b border-border px-6 lg:px-10 py-4 bg-background sticky top-0 z-50">
          <div className="flex items-center gap-2">
            <img src="/img/favicon.svg" alt="LIZTA" className="h-8 w-auto" />
            <span className="font-bold text-xl tracking-tight">LIZTA</span>
          </div>
        </header>
        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl font-medium mb-4">
              {error}
            </div>
            <p className="text-muted-foreground text-sm">
              Redirecionando para a página inicial...
            </p>
            <Button
              type="button"
              variant="link"
              onClick={handleManualRedirect}
              className="mt-6 text-sm font-medium"
            >
              Clique aqui para ir agora
            </Button>
          </div>
        </main>
        <footer className="py-6 text-center">
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} LIZTA</p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-border px-6 lg:px-10 py-4 bg-background sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <img src="/img/favicon.svg" alt="LIZTA" className="h-8 w-auto" />
          <span className="font-bold text-xl tracking-tight hidden sm:inline">LIZTA</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6 relative overflow-hidden">
        {/* Background blurs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] mix-blend-screen opacity-60 dark:opacity-40 animate-pulse"
            style={{ animationDuration: '4s' }}
          />
          <div
            className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/10 rounded-full blur-[100px] mix-blend-screen opacity-60 dark:opacity-40 animate-pulse"
            style={{ animationDuration: '5s', animationDelay: '1s' }}
          />
        </div>

        <div className="flex flex-col max-w-[480px] w-full items-center z-10">
          {/* Spinner */}
          <div className="mb-10 relative">
            <div className="size-24 rounded-full border-[6px] border-primary/20 flex items-center justify-center relative">
              <div
                className="absolute inset-0 rounded-full border-[6px] border-transparent border-t-primary animate-spin"
                style={{ animationDuration: '1.5s' }}
              />
              <Icon name="lock_open" />
            </div>
          </div>

          {/* Text */}
          <div className="flex flex-col gap-2 items-center text-center">
            <h1 className="text-foreground tracking-tight text-3xl font-bold leading-tight">
              Finalizando login...
            </h1>
            <p className="text-muted-foreground text-lg font-medium leading-normal max-w-sm">
              Verificando credenciais e preparando sua sessão segura.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="w-full mt-8 mb-6 px-8">
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full w-[85%] relative overflow-hidden">
                <div
                  className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  style={{
                    transform: 'skewX(-20deg)',
                    animation: 'auth-callback-shimmer 2s infinite',
                  }}
                />
              </div>
            </div>
            <div className="flex justify-between mt-2 px-1">
              <span className="text-xs font-medium text-muted-foreground">Autenticando</span>
              <span className="text-xs font-medium text-primary">Redirecionando</span>
            </div>
          </div>

          {/* Redirect Message */}
          <div className="mt-4 flex flex-col items-center gap-4 bg-muted/50 border border-border p-4 rounded-xl w-full backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <Icon name="rocket_launch" />
              <p className="text-muted-foreground text-sm font-medium">
                Aguarde um momento, estamos redirecionando você para sua área.
              </p>
            </div>
          </div>

          {/* Fallback Link */}
          <div className="mt-8 text-center">
            <Button
              type="button"
              variant="ghost"
              onClick={handleManualRedirect}
              className="group flex items-center gap-1.5 h-auto text-muted-foreground text-sm font-medium hover:text-primary hover:bg-transparent shadow-none mx-auto"
            >
              <span>Não foi redirecionado?</span>
              <span className="underline underline-offset-4 decoration-border group-hover:decoration-primary">
                Clique aqui
              </span>
              <Icon name="arrow_forward" />
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} LIZTA — Conexão segura
        </p>
      </footer>

      <style>{`
        @keyframes auth-callback-shimmer {
          0% { transform: skewX(-20deg) translateX(-150%); }
          100% { transform: skewX(-20deg) translateX(150%); }
        }
      `}</style>
    </div>
  );
}
